import React from "react";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import { NewReleasesOutlined, RefreshOutlined } from "@mui/icons-material";
import * as MuiIcons from "@mui/icons-material";
import { useModalStyles } from "../common/useModalStyles";
import { useAuthSlice } from "../../redux/slice/authSlice";
//@ts-ignore
import recordTxReminderDemo from "../../assets/videos/recordtx_reminders-demo.mp4";
//@ts-ignore
import icd10SearchDemo from "../../assets/videos/ICD10_Search.mp4";

const videoAssets: any = {
  "recordtx_reminders-demo.mp4": recordTxReminderDemo,
  "ICD10_Search.mp4": icd10SearchDemo,
};

export type ReleaseNotePopupProps = {
  open: boolean;
  onSubmit?: () => void;
  version?: string;
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const TAG_PRESETS = [
  { label: "New feature",  bgD: "rgba(20,184,166,.10)",  colorD: "#5EEAD4", bdD: "rgba(20,184,166,.18)",  bgL: "rgba(13,148,136,0.08)",  colorL: "#0f766e", bdL: "rgba(13,148,136,0.18)"  },
  { label: "Improved",     bgD: "rgba(99,102,241,.10)",  colorD: "#A5B4FC", bdD: "rgba(99,102,241,.18)",  bgL: "rgba(99,102,241,0.08)",  colorL: "#4338CA", bdL: "rgba(99,102,241,0.18)"  },
  { label: "Performance",  bgD: "rgba(245,158,11,.08)",  colorD: "#FCD34D", bdD: "rgba(245,158,11,.15)",  bgL: "rgba(245,158,11,0.08)",  colorL: "#b45309", bdL: "rgba(245,158,11,0.18)"  },
  { label: "Fix",          bgD: "rgba(239,68,68,.08)",   colorD: "#FCA5A5", bdD: "rgba(239,68,68,.15)",   bgL: "rgba(239,68,68,0.08)",   colorL: "#b91c1c", bdL: "rgba(239,68,68,0.18)"   },
  { label: "Enhancement",  bgD: "rgba(139,92,246,.10)",  colorD: "#C4B5FD", bdD: "rgba(139,92,246,.18)",  bgL: "rgba(139,92,246,0.08)",  colorL: "#6d28d9", bdL: "rgba(139,92,246,0.18)"  },
];

const ReleaseNotePopup: React.FC<ReleaseNotePopupProps> = ({
  open,
  onSubmit,
  version,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { isDark, paperSx, primaryBtnSx } = useModalStyles();
  const { userInfo } = useAuthSlice();
  const releaseNoteRaw = userInfo?.releaseNote;

  let releaseNote: any = null;
  if (releaseNoteRaw !== null) {
    try {
      releaseNote =
        typeof releaseNoteRaw === "string"
          ? JSON.parse(releaseNoteRaw)
          : releaseNoteRaw;
    } catch {
      releaseNote = null;
    }
  }

  const ACCENT       = "#14B8A6";
  const cardBg       = isDark ? "#111827" : "#ffffff";
  const cardBorder   = isDark ? "rgba(255,255,255,0.06)" : "#e5e9ed";
  const cardShadow   = isDark
    ? "0 0 0 1px rgba(255,255,255,.02), 0 8px 24px rgba(0,0,0,.6), 0 40px 80px rgba(0,0,0,.5)"
    : "0 1px 2px rgba(15,23,42,0.04), 0 2px 8px rgba(15,23,42,0.06)";

  const heroBg       = isDark ? "transparent" : "#fafbfc";
  const heroBdr      = isDark ? "rgba(255,255,255,.05)" : "#e5e9ed";

  const badgeBg      = isDark ? "rgba(20,184,166,.12)" : "rgba(13,148,136,0.10)";
  const badgeBdr     = isDark ? "rgba(20,184,166,.20)" : "rgba(13,148,136,0.25)";

  const eyebrowColor = ACCENT;
  const titleColor   = isDark ? "#F1F5F9" : "#0f172a";
  const subColor     = isDark ? "#475569" : "#64748b";
  const dateColor    = isDark ? "#334155" : "#94a3b8";

  const versionPillBg  = isDark ? "rgba(99,102,241,.12)"  : "rgba(99,102,241,0.08)";
  const versionPillClr = isDark ? "#A5B4FC"               : "#4338CA";
  const versionPillBdr = isDark ? "rgba(99,102,241,.22)"  : "rgba(99,102,241,0.20)";
  const typePillBg     = isDark ? "rgba(20,184,166,.10)"  : "rgba(13,148,136,0.08)";
  const typePillClr    = isDark ? "#5EEAD4"               : "#0f766e";
  const typePillBdr    = isDark ? "rgba(20,184,166,.18)"  : "rgba(13,148,136,0.20)";

  const listLabelClr = isDark ? "#334155" : "#94a3b8";

  const fiBg     = isDark ? "rgba(255,255,255,.025)" : "#fafbfc";
  const fiBdr    = isDark ? "rgba(255,255,255,.045)" : "#e5e9ed";
  const fiNumBg  = isDark ? "rgba(255,255,255,.06)"  : "#f1f5f9";
  const fiNumClr = isDark ? "#64748B"                : "#64748b";
  const fiTitle  = isDark ? "#E2E8F0" : "#1e293b";
  const fiDesc   = isDark ? "#CBD5E1" : "#475569";

  const footerBdr  = isDark ? "rgba(255,255,255,.05)" : "#e5e9ed";
  const footerBg   = isDark ? "transparent"           : "#fafbfc";
  const footerText = isDark ? "#5EEAD4" : "#0f766e";

  const noteBg  = isDark ? "rgba(245,158,11,.07)" : "#fffbeb";
  const noteBdr = isDark ? "rgba(245,158,11,.15)" : "#fde68a";
  const noteClr = isDark ? "#FCD34D"              : "#92400e";

  const nestedDot = isDark ? "#3B82F6" : "#3b82f6";

  const ReloadButton = ({ small = false }: { small?: boolean }) => (
    <Button
      variant="contained"
      startIcon={<RefreshOutlined sx={{ fontSize: small ? 13 : 15 }} />}
      onClick={onSubmit}
      sx={{
        ...primaryBtnSx,
        background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
        boxShadow: "none",
        "&:hover": { background: "linear-gradient(135deg, #0d9488 0%, #0f766e 100%)", boxShadow: "none" },
        py: small ? 0.5 : 0.9,
        px: small ? 1.5 : 2.5,
        fontSize: small ? "0.73rem" : "0.82rem",
        fontWeight: 700,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {small ? "Reload" : "Reload now"}
    </Button>
  );

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: {
        ...paperSx(isMobile),
        ...(isMobile && { borderRadius: 0, maxHeight: "100vh", m: 0 }),
      }}}
      BackdropProps={{
        sx: {
          backgroundColor: isDark ? "rgba(20,23,32,0.75)" : "rgba(20,23,30,0.45)",
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <Box sx={{
        display: "flex", alignItems: "flex-start", gap: 1.75,
        px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.75 }, pb: { xs: 1.75, sm: 2.5 },
        background: heroBg,
        borderBottom: `1px solid ${heroBdr}`,
        flexShrink: 0,
        position: "relative",
      }}>
        <Box sx={{
          width: { xs: 38, sm: 46 }, height: { xs: 38, sm: 46 }, borderRadius: "14px",
          background: badgeBg, border: `1px solid ${badgeBdr}`,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <NewReleasesOutlined sx={{ fontSize: { xs: 19, sm: 22 }, color: ACCENT }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: eyebrowColor, mb: 0.4, lineHeight: 1 }}>
            New release available
          </Typography>

          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5 }}>
            <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: titleColor, lineHeight: 1.25, mb: 0.5 }}>
              Web app has been updated
            </Typography>
            <ReloadButton small />
          </Box>

          <Typography sx={{ fontSize: "0.78rem", color: subColor, lineHeight: 1.6, mb: 1, display: { xs: "none", sm: "block" } }}>
            A new version is ready. Reload to get the latest features and fixes.
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
            {version && (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, px: 1.1, py: 0.4, borderRadius: "20px", background: versionPillBg, border: `1px solid ${versionPillBdr}` }}>
                <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: isDark ? "#818CF8" : "#6366F1", flexShrink: 0 }} />
                <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: versionPillClr }}>v{version}</Typography>
              </Box>
            )}
            {releaseNote?.features && (
              <Box sx={{ display: { xs: "none", sm: "inline-flex" }, alignItems: "center", gap: 0.6, px: 1.1, py: 0.4, borderRadius: "20px", background: typePillBg, border: `1px solid ${typePillBdr}` }}>
                <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: ACCENT, flexShrink: 0 }} />
                <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: typePillClr }}>{releaseNote.features}</Typography>
              </Box>
            )}
            {releaseNote?.date && (
              <Typography sx={{ fontSize: "0.68rem", ml: "auto" }}>
                {releaseNote.date}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{
        px: { xs: 1.5, sm: 3 }, pt: 2, pb: 1,
        flex: 1,
        overflowY: "auto",
        "&::-webkit-scrollbar": { width: 3 },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": { background: isDark ? "rgba(255,255,255,.08)" : "#CBD5E1", borderRadius: 3 },
      }}>
        {releaseNote?.subtitle && (
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: titleColor, mb: 1.5 }}>
            {releaseNote.subtitle}
          </Typography>
        )}

        {releaseNote?.details?.length > 0 && (
          <Typography sx={{ fontSize: "0.67rem", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: listLabelClr, mb: 1.25 }}>
            What's new
          </Typography>
        )}

        {releaseNote?.details?.map((item: any, index: number) => {
          const preset =
            TAG_PRESETS.find((p) => p.label === item.tag) ??
            TAG_PRESETS[index % TAG_PRESETS.length];
          const tagBg  = isDark ? preset.bgD  : preset.bgL;
          const tagClr = isDark ? preset.colorD : preset.colorL;
          const tagBdr = isDark ? preset.bdD  : preset.bdL;
          //@ts-ignore
          const BulletIcon: any = item.bulletIcon ? MuiIcons[item.bulletIcon] : null;
          //@ts-ignore
          const InlineIcon: any = item.icon ? MuiIcons[item.icon] : null;
          const num = String(index + 1).padStart(2, "0");

          return (
            <Box key={index} sx={{
              display: "flex", alignItems: "flex-start", gap: { xs: 1, sm: 1.5 },
              p: { xs: "10px", sm: "13px 14px 13px 12px" }, borderRadius: "12px", mb: 1,
              background: fiBg,
              border: `1px solid ${fiBdr}`,
              transition: "background .15s",
              "&:hover": { background: isDark ? "rgba(255,255,255,.05)" : "#f1f5f9" },
            }}>
              {/* Number / bullet icon */}
              {BulletIcon ? (
                <Box sx={{ width: 24, height: 24, mt: "2px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <BulletIcon sx={{ fontSize: 16, color: tagClr }} />
                </Box>
              ) : (
                <Box sx={{
                  fontSize: "0.65rem", fontWeight: 700,
                  width: 22, height: 22, borderRadius: "6px",
                  background: fiNumBg, color: fiNumClr,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, mt: "2px",
                }}>
                  {num}
                </Box>
              )}

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: fiTitle, lineHeight: 1.3, mb: 0.4 }}>
                  {item.heading}
                </Typography>

                <Typography sx={{ fontSize: "0.8rem", color: fiDesc, lineHeight: 1.7, mb: 0.75 }}>
                  {item.description?.includes("{{icon}}") && InlineIcon ? (
                    <>
                      {item.description.split("{{icon}}")[0]}
                      <InlineIcon sx={{ fontSize: "0.9rem", verticalAlign: "middle", mx: 0.4, color: item.iconColor || tagClr }} />
                      {item.description.split("{{icon}}")[1]}
                    </>
                  ) : item.description}
                </Typography>

                {/* Type tag */}
                <Box sx={{ display: "inline-flex", alignItems: "center", px: 1, py: 0.25, borderRadius: "20px", background: tagBg, border: `1px solid ${tagBdr}` }}>
                  <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: tagClr }}>{preset.label}</Typography>
                </Box>

                {/* Video */}
                {(item.videoAsset || item.videoBase64) && (
                  <Box sx={{ mt: 1.5, borderRadius: "10px", overflow: "hidden", border: `1px solid ${isDark ? "rgba(255,255,255,.06)" : "#E2E8F0"}` }}>
                    <video controls autoPlay={item.autoPlay || false} muted={item.muted !== false} loop={item.loop || false}
                      style={{ width: "100%", maxHeight: 280, display: "block" }} poster={item.videoPoster}>
                      <source src={videoAssets[item.videoAsset] || item.videoBase64 || item.videoUrl} type={item.videoType || "video/mp4"} />
                    </video>
                    {item.videoCaption && (
                      <Typography sx={{ fontSize: "0.68rem", color: subColor, fontStyle: "italic", px: 1.5, py: 0.75 }}>{item.videoCaption}</Typography>
                    )}
                  </Box>
                )}

                {/* Image */}
                {item.imageBase64 && (
                  <Box sx={{ mt: 1.5, borderRadius: "10px", overflow: "hidden", border: `1px solid ${isDark ? "rgba(255,255,255,.06)" : "#E2E8F0"}` }}>
                    <img src={item.imageBase64} alt={item.heading} style={{ width: "100%", maxHeight: 180, objectFit: "cover", display: "block" }} />
                    {item.imageCaption && (
                      <Typography sx={{ fontSize: "0.68rem", color: subColor, fontStyle: "italic", px: 1.5, py: 0.75 }}>{item.imageCaption}</Typography>
                    )}
                  </Box>
                )}

                {/* Why This Matters */}
                {item.whyThisMatters && (
                  <Box sx={{ mt: 1.25, p: "10px 13px", borderRadius: "9px", background: isDark ? "rgba(255,255,255,.03)" : "#f8fafc", border: `1px solid ${isDark ? "rgba(255,255,255,.05)" : "#e2e8f0"}` }}>
                    <Typography sx={{ fontSize: "0.73rem", fontWeight: 600, color: fiTitle, mb: 0.75 }}>{item.whyThisMatters.title}</Typography>
                    {item.whyThisMatters.points.map((pt: string, i: number) => (
                      <Box key={i} sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                        <Box sx={{ width: 4, height: 4, borderRadius: "50%", background: nestedDot, mt: "6px", flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "0.72rem", color: fiDesc, lineHeight: 1.6 }}>{pt}</Typography>
                      </Box>
                    ))}
                    {item.whyThisMatters.conclusion && (
                      <Typography sx={{ fontSize: "0.72rem", color: fiDesc, mt: 0.5 }}>{item.whyThisMatters.conclusion}</Typography>
                    )}
                  </Box>
                )}

                {/* Nested sections */}
                {item.nestedSections?.map((section: any, si: number) => (
                  <Box key={si} sx={{ mt: 1.25 }}>
                    <Typography sx={{ fontSize: "0.73rem", fontWeight: 600, color: fiTitle, mb: 0.5 }}>{section.subheading}</Typography>
                    {section.points.map((pt: any, pi: number) => {
                      const isNested = typeof pt === "object" && pt.subPoints;
                      return (
                        <Box key={pi}>
                          <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                            <Box sx={{ width: 4, height: 4, borderRadius: "50%", background: nestedDot, mt: "6px", flexShrink: 0 }} />
                            <Typography sx={{ fontSize: "0.72rem", color: fiDesc, lineHeight: 1.6 }}>{isNested ? pt.text : pt}</Typography>
                          </Box>
                          {isNested && pt.subPoints?.map((sp: string, spi: number) => (
                            <Box key={spi} sx={{ display: "flex", gap: 1, mb: 0.4, pl: 2 }}>
                              <Box sx={{ width: 3, height: 3, borderRadius: "50%", background: fiDesc, mt: "7px", flexShrink: 0, opacity: 0.4 }} />
                              <Typography sx={{ fontSize: "0.71rem", color: fiDesc, lineHeight: 1.6, opacity: 0.8 }}>{sp}</Typography>
                            </Box>
                          ))}
                        </Box>
                      );
                    })}
                  </Box>
                ))}

                {/* Note */}
                {item.note && (
                  <Box sx={{ mt: 1.25, px: 1.25, py: 0.9, borderRadius: "8px", background: noteBg, border: `1px solid ${noteBdr}` }}>
                    <Typography sx={{ fontSize: "0.71rem", color: noteClr, lineHeight: 1.6 }}>
                      <Box component="span" sx={{ fontWeight: 700 }}>Note: </Box>
                      {typeof item.note === "string" ? item.note : (
                        <>
                          {item.note.text?.split("{{link}}")[0]}
                          {item.note.link && (
                            <Box component="a" href={item.note.link.url} target="_blank" rel="noopener noreferrer"
                              sx={{ color: "#3B82F6", textDecoration: "underline", cursor: "pointer" }}>
                              {item.note.link.text}
                            </Box>
                          )}
                          {item.note.text?.split("{{link}}")[1]}
                        </>
                      )}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ── Footer ── */}
      <Box sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        justifyContent: "space-between",
        gap: { xs: 1.5, sm: 2 },
        px: { xs: 2, sm: 3 }, pt: 1.75, pb: 2.25,
        background: footerBg,
        borderTop: `1px solid ${footerBdr}`,
        flexShrink: 0,
      }}>
        {/* "Coming soon" animated callout */}
        <Box sx={{
          display: "inline-flex", alignItems: "center", gap: 1,
          px: 1.5, py: 0.75, borderRadius: "10px",
          background: isDark ? "rgba(20,184,166,.08)" : "rgba(13,148,136,0.06)",
          border: `1px solid ${isDark ? "rgba(20,184,166,.18)" : "rgba(13,148,136,0.15)"}`,
          position: "relative",
          overflow: "hidden",
          justifyContent: { xs: "center", sm: "flex-start" },
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0, left: "-100%",
            width: "60%", height: "100%",
            background: isDark
              ? "linear-gradient(90deg, transparent, rgba(94,234,212,.12), transparent)"
              : "linear-gradient(90deg, transparent, rgba(13,148,136,.10), transparent)",
            animation: "rnp-shimmer 2.6s ease-in-out infinite",
          },
          "@keyframes rnp-shimmer": {
            "0%":   { left: "-60%" },
            "60%":  { left: "140%" },
            "100%": { left: "140%" },
          },
        }}>
          {/* Animated pulse dot */}
          <Box sx={{ position: "relative", width: 8, height: 8, flexShrink: 0 }}>
            <Box sx={{
              position: "absolute", inset: -2,
              borderRadius: "50%",
              background: ACCENT,
              opacity: 0.25,
              animation: "rnp-ping 1.6s cubic-bezier(0,0,.2,1) infinite",
              "@keyframes rnp-ping": {
                "0%":   { transform: "scale(1)",   opacity: 0.25 },
                "80%":  { transform: "scale(2.2)", opacity: 0 },
                "100%": { transform: "scale(2.2)", opacity: 0 },
              },
            }} />
            <Box sx={{
              position: "absolute", inset: 0,
              borderRadius: "50%",
              background: ACCENT,
              boxShadow: `0 0 5px ${ACCENT}99`,
            }} />
          </Box>

          <Typography sx={{
            fontSize: { xs: "0.72rem", sm: "0.75rem" }, fontWeight: 600,
            color: isDark ? "#5EEAD4" : "#0f766e",
            letterSpacing: "0.01em",
            position: "relative", zIndex: 1,
          }}>
            More exciting updates are coming soon
          </Typography>

          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{
              width: 3, height: 3, borderRadius: "50%",
              background: isDark ? "#5EEAD4" : "#0d9488",
              opacity: 0,
              flexShrink: 0,
              animation: `rnp-blink 1.4s ${i * 0.22}s ease-in-out infinite`,
              "@keyframes rnp-blink": {
                "0%,100%": { opacity: 0,   transform: "scale(.6)" },
                "50%":     { opacity: 0.7, transform: "scale(1)"   },
              },
            }} />
          ))}
        </Box>

        <Box sx={{ display: "flex", justifyContent: { xs: "center", sm: "flex-end" } }}>
          <ReloadButton />
        </Box>
      </Box>
    </Dialog>
  );
};

export default ReleaseNotePopup;